import type { BotFlags } from '../../api/types';

/**
 * What a new assistant is FOR. The first question the setup flow asks, because
 * the answer decides everything else on the form — an assistant that only
 * answers questions wants different guardrails from one that quotes prices, and
 * an operator staring at an empty Business context box has no way to know that.
 *
 * Every preset is a starting point, not a mode: nothing here is enforced later,
 * and all of it is editable the moment setup finishes.
 */
export interface BotPreset {
  id: 'answer' | 'quote' | 'after_hours';
  label: string;
  blurb: string;
  /** What the preset changes, in the operator's words — shown on the card. */
  effects: string[];
  systemContext: string;
  guardrails: string[];
  flags: BotFlags;
  handoffMessage: string;
  resolutionMessage: string;
}

export const BOT_PRESETS: BotPreset[] = [
  {
    id: 'answer',
    label: 'Answer common questions',
    blurb: 'Hours, service areas, policies, what to expect. Hands anything else to your team.',
    effects: ['No prices', 'No account lookups', 'Hands off early'],
    systemContext:
      'We are a chauffeured ground-transportation company. Answer questions about our service, coverage, vehicles and policies. Anything about a specific reservation, a price, or a change goes to a human.',
    guardrails: [
      'Never quote a price or estimate one — offer to have a person confirm it.',
      'Never promise a specific chauffeur or vehicle.',
      'Hand off immediately if the customer mentions a complaint, a refund or a cancellation.',
    ],
    flags: { allowRateEstimates: false, allowDataAccessLoggedIn: false },
    handoffMessage: 'One moment — I am bringing a member of our team into this conversation.',
    resolutionMessage: 'Glad we could help! Message us any time.',
  },
  {
    id: 'quote',
    label: 'Quote and capture reservations',
    blurb:
      'Collects the trip — pickup, dropoff, date, passengers — and quotes every vehicle from your rate engine.',
    effects: ['Rate estimates on', 'Asks for trip details', 'Hands off to close'],
    systemContext:
      'We are a chauffeured ground-transportation company. Help customers price a trip: collect the pickup, the dropoff, the date and time, the passenger count, and for an hourly reservation the number of hours. Quote the vehicles our rate engine returns, and hand the customer to a person to confirm and book.',
    guardrails: [
      'Only quote prices that came from the rate tool — never estimate one yourself.',
      'Always say a quote is an estimate until a person confirms it.',
      'Never take card details or promise a reservation is confirmed.',
    ],
    flags: { allowRateEstimates: true, allowDataAccessLoggedIn: false },
    handoffMessage: 'Let me bring in a colleague to confirm this and get you booked.',
    resolutionMessage: 'Thanks for the details — talk soon!',
  },
  {
    id: 'after_hours',
    label: 'Cover after hours',
    blurb:
      'Answers what it safely can overnight, takes the details for everything else, and closes the thread if nobody replies.',
    effects: ['Auto-resolves when idle', 'Takes a message', 'Sets expectations on timing'],
    systemContext:
      'We are a chauffeured ground-transportation company. You are answering outside office hours. Answer general questions, take the details of anything that needs a person, and tell the customer when the team will be back to them. Never leave a customer thinking something is booked.',
    guardrails: [
      'Say clearly that the office is closed and when someone will reply.',
      'Never confirm, change or cancel a reservation — take the details and say a person will handle it.',
      'Hand off immediately if the trip is today or the customer says it is urgent.',
    ],
    flags: { allowRateEstimates: false, allowDataAccessLoggedIn: false, autoResolveAfterMinutes: 60 },
    handoffMessage: 'I am passing this to our team — they will pick it up as soon as they are in.',
    resolutionMessage: 'Closing this for now — reply any time and we will pick it straight back up.',
  },
];
